import {
  defaultWaiverText,
  HEALTH_QUESTION_TEXT_MAX,
  HEALTH_QUESTIONS_MAX,
  newHealthQuestionId,
  WAIVER_TEXT_MAX,
  type HealthForm,
  type HealthQuestion,
  type UpdateHealthFormRequest,
} from "@teamna/shared";
import { useState } from "react";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { useHealthForm, useUpdateHealthForm } from "@teamna/web-client/hooks/use-health.ts";
import {
  Button,
  CellField,
  ErrorState,
  FormRow,
  FormSection,
  InlineError,
  Notice,
  SkeletonList,
  TextAreaField,
  ToggleRow,
  showToast,
} from "../components/index.ts";
import { SaveBar } from "./setup/frame.tsx";

/**
 * Settings, Health (C2): the health questions and the terms that new people answer when they join.
 * Off by default. The owner turns it on, edits the questions Teamna offers (add, change, remove),
 * writes the terms (a change makes a new version), and chooses if staff read the answers.
 *
 * People who answered before a change keep their answers and the version they accepted. V1 does not
 * ask them again, and the panel says so.
 *
 * `HealthSettingsView` takes plain props, so Storybook draws it; `HealthSettings` reads and saves.
 */

export function HealthSettings({ businessId }: { businessId: string }) {
  const form = useHealthForm(businessId);
  const update = useUpdateHealthForm(businessId);
  if (form.isLoading) return <SkeletonList rows={4} />;
  if (form.error || !form.data) return <ErrorState error={form.error} onRetry={() => void form.refetch()} />;
  return (
    <HealthSettingsView
      key={JSON.stringify([form.data.questions, form.data.waiver?.version ?? null])}
      form={form.data}
      saving={update.isPending}
      error={update.error}
      onSave={(body, done) =>
        update.mutate(body, {
          onSuccess: () => {
            showToast(done);
          },
        })
      }
    />
  );
}

export function HealthSettingsView({
  form,
  saving = false,
  error = null,
  onSave,
}: {
  form: HealthForm;
  saving?: boolean;
  error?: unknown;
  /** The change, and the words of the toast when it lands. */
  onSave: (body: UpdateHealthFormRequest, toast: string) => void;
}) {
  const { label } = useLabels();
  const { forChildren } = useShape();
  const [questions, setQuestions] = useState<HealthQuestion[]>(form.questions);
  const [terms, setTerms] = useState(form.waiver?.text ?? defaultWaiverText);
  const staff = label("staff", { plural: true, capital: true });

  const savedQuestions = JSON.stringify(form.questions);
  const questionsChanged = JSON.stringify(questions.map((question) => ({ ...question, text: question.text.trim() }))) !== savedQuestions;
  const questionsValid = questions.length > 0 && questions.every((question) => question.text.trim().length >= 3);
  const termsChanged = terms.trim() !== (form.waiver?.text ?? "");

  return (
    <div className="flex flex-col gap-6">
      <FormSection
        title="Health questions at join"
        description={`New ${label("participant", { plural: true })} answer short yes or no questions when they join, and accept your terms.${forChildren ? ` A ${label("member")} answers for each ${label("participant")}.` : ""}`}
      >
        <ToggleRow
          label="Ask health questions when people join"
          hint={
            form.enabled
              ? `On. ${form.answeredCount === 1 ? "1 person answered" : `${form.answeredCount} people answered`} so far. People who were here before you turned it on are not asked.`
              : "Off. Nobody is asked. Turn it on to ask the questions below."
          }
          checked={form.enabled}
          disabled={saving}
          onChange={(next) => onSave({ enabled: next }, next ? "Health questions are on." : "Health questions are off.")}
        />
        <ToggleRow
          label={`${staff} can read health notes`}
          hint={
            form.staffCanRead
              ? `On: ${label("staff", { plural: true })} read the answers. The owner and the admins always can.`
              : `Off: ${label("staff", { plural: true })} see only the flag "Health note" on the class list. The owner and the admins read the answers.`
          }
          checked={form.staffCanRead}
          disabled={saving}
          onChange={(next) => onSave({ staffCanRead: next }, next ? `${staff} can read health notes.` : `${staff} see the flag only.`)}
        />
      </FormSection>

      <FormSection title="Questions" description="Each question has a yes or a no. A yes asks for a short note.">
        {questions.map((question, index) => (
          <FormRow key={question.id} label={`Question ${index + 1}`}>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <CellField
                  label={`Question ${index + 1}`}
                  value={question.text}
                  maxLength={HEALTH_QUESTION_TEXT_MAX}
                  dir="auto"
                  aria-invalid={question.text.trim().length < 3 ? true : undefined}
                  onChange={(event) =>
                    setQuestions((current) => current.map((row) => (row.id === question.id ? { ...row, text: event.target.value } : row)))
                  }
                />
              </div>
              <Button
                variant="ghost"
                disabled={questions.length <= 1}
                onClick={() => setQuestions((current) => current.filter((row) => row.id !== question.id))}
              >
                Remove
              </Button>
            </div>
          </FormRow>
        ))}
        <div className="py-4">
          <Button
            variant="secondary"
            disabled={questions.length >= HEALTH_QUESTIONS_MAX}
            onClick={() => setQuestions((current) => [...current, { id: newHealthQuestionId(current), text: "" }])}
          >
            Add a question
          </Button>
        </div>
      </FormSection>
      <SaveBar note={questionsChanged ? (questionsValid ? undefined : "Each question needs 3 letters or more.") : "No changes yet."}>
        <Button
          disabled={!questionsChanged || !questionsValid}
          loading={saving}
          onClick={() => onSave({ questions: questions.map((question) => ({ id: question.id, text: question.text.trim() })) }, "Questions saved.")}
        >
          Save questions
        </Button>
        {questionsChanged ? (
          <Button variant="secondary" onClick={() => setQuestions(form.questions)}>
            Undo changes
          </Button>
        ) : null}
      </SaveBar>

      <FormSection
        title="Terms"
        description={form.waiver ? `Version ${form.waiver.version}. A change makes the next version.` : "No terms yet. Teamna writes a start for you below: read it and change it."}
      >
        <div className="py-4">
          <TextAreaField
            label="Your terms"
            value={terms}
            rows={6}
            maxLength={WAIVER_TEXT_MAX}
            dir="auto"
            hint="People read these words and accept them when they join. Leave it empty for no terms."
            onChange={(event) => setTerms(event.target.value)}
          />
        </div>
      </FormSection>
      {form.waiver && termsChanged ? (
        <Notice tone="info" title={`This makes version ${form.waiver.version + 1}`}>
          People who accepted version {form.waiver.version} keep it. They are not asked again. New people accept the new version.
        </Notice>
      ) : null}
      <SaveBar note={termsChanged ? undefined : "No changes yet."}>
        <Button disabled={!termsChanged} loading={saving} onClick={() => onSave({ waiverText: terms }, "Terms saved.")}>
          Save terms
        </Button>
        {termsChanged ? (
          <Button variant="secondary" onClick={() => setTerms(form.waiver?.text ?? defaultWaiverText)}>
            Undo changes
          </Button>
        ) : null}
      </SaveBar>
      {error ? <InlineError error={error} /> : null}
    </div>
  );
}
